import { useCallback, useState } from 'react';
import { useTranslations } from 'next-intl';
import { useQueryClient, type QueryClient } from '@tanstack/react-query';
import {
  canEditEndpointMemory,
  canRemoveEndpointMemory,
  toasterApi,
} from '@terra/ui-design-system';
import type {
  ListMemoryRecordsResponse,
  MemoryRecordResponse,
} from '@terra/shared-contracts-memory';
import {
  EndpointMemoryCreateRejectedError,
  terraProxyApiClient,
} from '../../clients/testerClient';
import {
  ENDPOINT_MEMORY_OVERLAYS_QUERY_KEY,
  createPendingMemoryOverlay,
  createRejectedMemoryOverlay,
  isSyntheticMemoryRecordId,
  toMemorySnapshot,
  writePersistedEndpointMemoryOverlays,
  type EndpointMemoryOverlay,
  type MemorySnapshotRecord,
} from '../endpointMemoryOverlays';
import {
  ENDPOINT_MEMORIES_QUERY_KEY,
  getUnfilteredEndpointMemoriesQueryKey,
} from './useEndpointMemories';

type UseEndpointMemoryActionsParams = {
  applicationId: string | undefined;
  endpointId: string | undefined;
  onSuccess?: () => void;
};

type ActionState = {
  isSubmitting: boolean;
  actionInProgress: string | null;
};

type UseEndpointMemoryActionsReturn = {
  isSubmitting: boolean;
  createMemory: (
    content: string,
    options?: { replaceRecordId?: string },
  ) => Promise<boolean>;
  updateMemory: (recordId: string, content: string) => Promise<boolean>;
  deleteMemory: (memory: MemoryRecordResponse) => Promise<boolean>;
  isActionInProgress: (recordId: string) => boolean;
};

const setEndpointMemoryOverlays = ({
  queryClient,
  applicationId,
  endpointId,
  updater,
}: {
  queryClient: QueryClient;
  applicationId: string;
  endpointId: string;
  updater: (current: EndpointMemoryOverlay[]) => EndpointMemoryOverlay[];
}) => {
  const overlaysQueryKey = [
    ENDPOINT_MEMORY_OVERLAYS_QUERY_KEY,
    applicationId,
    endpointId,
  ] as const;
  const nextOverlays = updater(
    queryClient.getQueryData<EndpointMemoryOverlay[]>(overlaysQueryKey) ?? [],
  );

  queryClient.setQueryData(overlaysQueryKey, nextOverlays);
  writePersistedEndpointMemoryOverlays({
    applicationId,
    endpointId,
    overlays: nextOverlays,
  });
};

const loadBeforeSnapshot = async ({
  queryClient,
  applicationId,
  endpointId,
}: {
  queryClient: QueryClient;
  applicationId: string;
  endpointId: string;
}): Promise<MemorySnapshotRecord[]> => {
  const memoriesData = await queryClient
    .ensureQueryData({
      queryKey: getUnfilteredEndpointMemoriesQueryKey({
        applicationId,
        endpointId,
      }),
      queryFn: () =>
        terraProxyApiClient.getMemoriesByEndpoint(applicationId, endpointId, {
          skip: 0,
          limit: 250,
        }),
    })
    .catch(() => undefined);

  return toMemorySnapshot(memoriesData?.records ?? []);
};

export function useEndpointMemoryActions({
  applicationId,
  endpointId,
  onSuccess,
}: UseEndpointMemoryActionsParams): UseEndpointMemoryActionsReturn {
  const queryClient = useQueryClient();
  const t = useTranslations('memories.endpointMemories.toast');
  const tRejection = useTranslations('memories.rejectionReason');

  const [actionState, setActionState] = useState<ActionState>({
    isSubmitting: false,
    actionInProgress: null,
  });

  const createMemory = useCallback(
    async (
      content: string,
      { replaceRecordId }: { replaceRecordId?: string } = {},
    ): Promise<boolean> => {
      if (!applicationId || !endpointId || !content.trim()) {
        return false;
      }

      setActionState({
        isSubmitting: true,
        actionInProgress: replaceRecordId ?? 'create',
      });

      try {
        const beforeSnapshot = await loadBeforeSnapshot({
          queryClient,
          applicationId,
          endpointId,
        });

        const result = await terraProxyApiClient.createEndpointMemoryRecord(
          applicationId,
          endpointId,
          content.trim(),
        );

        if (result.recordId) {
          if (replaceRecordId) {
            setEndpointMemoryOverlays({
              queryClient,
              applicationId,
              endpointId,
              updater: (current) =>
                current.filter((item) => item.recordId !== replaceRecordId),
            });
          }

          await queryClient.invalidateQueries({
            queryKey: [ENDPOINT_MEMORIES_QUERY_KEY],
          });
        } else {
          if (!result.sessionId) {
            throw new Error('Create succeeded without sessionId or recordId');
          }

          const overlay = createPendingMemoryOverlay({
            sessionId: result.sessionId,
            content: content.trim(),
            beforeSnapshot,
          });

          setEndpointMemoryOverlays({
            queryClient,
            applicationId,
            endpointId,
            updater: (current) => [
              overlay,
              ...current.filter((item) => item.recordId !== replaceRecordId),
            ],
          });
        }

        toasterApi.success({
          title: t('createSuccessTitle'),
          description: t('createSuccessDescription'),
        });
        onSuccess?.();

        return true;
      } catch (error: unknown) {
        if (
          applicationId &&
          endpointId &&
          error instanceof EndpointMemoryCreateRejectedError
        ) {
          const rejectionReason =
            error.rejectionReason.trim() || tRejection('categoryMismatch');
          const overlay = createRejectedMemoryOverlay({
            content: content.trim(),
            rejectionReason,
          });

          setEndpointMemoryOverlays({
            queryClient,
            applicationId,
            endpointId,
            updater: (current) => [
              overlay,
              ...current.filter((item) => item.recordId !== replaceRecordId),
            ],
          });
          onSuccess?.();
          toasterApi.error({
            title: t('createErrorTitle'),
            description: rejectionReason,
          });

          return true;
        }

        toasterApi.error({
          title: t('createErrorTitle'),
          description: t('createErrorDescription'),
        });

        return false;
      } finally {
        setActionState({ isSubmitting: false, actionInProgress: null });
      }
    },
    [applicationId, endpointId, onSuccess, queryClient, t, tRejection],
  );

  const updateMemory = useCallback(
    async (recordId: string, content: string): Promise<boolean> => {
      if (!applicationId || !endpointId || !content.trim()) {
        return false;
      }

      setActionState({
        isSubmitting: true,
        actionInProgress: recordId,
      });

      try {
        const beforeSnapshot = await loadBeforeSnapshot({
          queryClient,
          applicationId,
          endpointId,
        });

        const existing = queryClient
          .getQueryData<ListMemoryRecordsResponse>(
            getUnfilteredEndpointMemoriesQueryKey({
              applicationId,
              endpointId,
            }),
          )
          ?.records.find((record) => record.recordId === recordId);

        if (existing && !canEditEndpointMemory(existing)) {
          return false;
        }

        const sessionId = `endpoint-memory-correction-${recordId}-${Date.now()}`;

        await terraProxyApiClient.updateEndpointMemoryRecord(
          applicationId,
          endpointId,
          recordId,
          content.trim(),
          sessionId,
        );

        const overlay = createPendingMemoryOverlay({
          sessionId,
          content: content.trim(),
          beforeSnapshot,
          replacesRecordId: recordId,
        });

        setEndpointMemoryOverlays({
          queryClient,
          applicationId,
          endpointId,
          updater: (current) => [
            overlay,
            ...current.filter(
              (item) =>
                item.recordId !== recordId &&
                item.replacesRecordId !== recordId,
            ),
          ],
        });

        await queryClient.invalidateQueries({
          queryKey: [ENDPOINT_MEMORIES_QUERY_KEY, applicationId, endpointId],
        });

        toasterApi.success({
          title: t('createSuccessTitle'),
          description: t('createSuccessDescription'),
        });
        onSuccess?.();

        return true;
      } catch {
        toasterApi.error({
          title: t('createErrorTitle'),
          description: t('createErrorDescription'),
        });

        return false;
      } finally {
        setActionState({ isSubmitting: false, actionInProgress: null });
      }
    },
    [applicationId, endpointId, onSuccess, queryClient, t],
  );

  const deleteMemory = useCallback(
    async (memory: MemoryRecordResponse): Promise<boolean> => {
      if (!applicationId || !endpointId) {
        return false;
      }

      if (!canRemoveEndpointMemory(memory)) {
        return false;
      }

      setActionState({
        isSubmitting: true,
        actionInProgress: memory.recordId,
      });

      try {
        if (isSyntheticMemoryRecordId(memory.recordId)) {
          setEndpointMemoryOverlays({
            queryClient,
            applicationId,
            endpointId,
            updater: (current) =>
              current.filter((overlay) => overlay.recordId !== memory.recordId),
          });
          toasterApi.success({
            title: t('deleteSuccessTitle'),
            description: t('deleteSuccessDescription'),
          });
          onSuccess?.();

          return true;
        }

        await terraProxyApiClient.deleteEndpointMemoryRecord(
          applicationId,
          endpointId,
          memory.recordId,
        );

        toasterApi.success({
          title: t('deleteSuccessTitle'),
          description: t('deleteSuccessDescription'),
        });

        await queryClient.invalidateQueries({
          queryKey: [ENDPOINT_MEMORIES_QUERY_KEY, applicationId, endpointId],
        });
        onSuccess?.();

        return true;
      } catch {
        toasterApi.error({
          title: t('deleteErrorTitle'),
          description: t('deleteErrorDescription'),
        });

        return false;
      } finally {
        setActionState({ isSubmitting: false, actionInProgress: null });
      }
    },
    [applicationId, endpointId, onSuccess, queryClient, t],
  );

  const isActionInProgress = useCallback(
    (recordId: string) => actionState.actionInProgress === recordId,
    [actionState.actionInProgress],
  );

  return {
    isSubmitting: actionState.isSubmitting,
    createMemory,
    updateMemory,
    deleteMemory,
    isActionInProgress,
  };
}
